import React, { useState, useEffect } from 'react';
import { 
  CreditCard, CheckCircle2, XCircle, Clock, User, MapPin, 
  ChevronLeft, RefreshCw, Search, Eye, DollarSign, AlertCircle,
  Receipt, QrCode, X, Image as ImageIcon, Banknote, Smartphone
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ordersAPI } from '../services/api';
import { toast } from 'sonner';

const PaymentPortal = ({ onBack, user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('pending'); // pending, confirmed, rejected
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showQR, setShowQR] = useState(null);

  // Fetch orders needing payment confirmation
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await ordersAPI.getAll();
      // Filter orders that need payment confirmation (exclude POS/in-store orders which are auto-confirmed)
      const paymentOrders = data.filter(order => 
        order.order_type !== 'in_store' && // Exclude POS orders
        !order.transfer_to_cashier && // Exclude transferred to cashier
        ['pending', 'payment_confirmed'].includes(order.status)
      );
      setOrders(paymentOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Confirm payment
  const confirmPayment = async (orderId) => {
    try {
      setProcessing(true);
      await ordersAPI.confirmPayment(orderId, {
        status: 'confirmed',
        notes: confirmationNotes
      });
      toast.success('Payment confirmed! Order ready for picking.');
      setSelectedOrder(null);
      setConfirmationNotes('');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to confirm payment');
    } finally {
      setProcessing(false);
    }
  };

  // Reject payment
  const rejectPayment = async (orderId) => {
    if (!confirmationNotes) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setProcessing(true);
      await ordersAPI.confirmPayment(orderId, {
        status: 'rejected',
        notes: confirmationNotes
      });
      toast.error('Payment rejected');
      setSelectedOrder(null);
      setConfirmationNotes('');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  // Get payment method display
  const getPaymentMethodDisplay = (method) => {
    const methods = {
      'cash': { label: 'Cash on Delivery', icon: Banknote, color: 'bg-green-100 text-green-700' },
      'etransfer': { label: 'E-Transfer', icon: Smartphone, color: 'bg-blue-100 text-blue-700' },
      'credit': { label: 'Credit Card', icon: CreditCard, color: 'bg-purple-100 text-purple-700' },
      'debit': { label: 'Debit Card', icon: CreditCard, color: 'bg-indigo-100 text-indigo-700' },
      'gift_card': { label: 'Gift Card', icon: Receipt, color: 'bg-pink-100 text-pink-700' },
    };
    return methods[method] || { label: method, icon: DollarSign, color: 'bg-gray-100 text-gray-700' };
  };

  // Filter orders based on search and filter
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.delivery_info?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'pending') return order.payment_status === 'pending' && matchesSearch;
    if (filter === 'confirmed') return order.payment_status === 'confirmed' && matchesSearch;
    if (filter === 'rejected') return order.payment_status === 'rejected' && matchesSearch;
    return matchesSearch;
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0a1628]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="payment-portal">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0a1628] flex items-center gap-2">
              <CreditCard className="w-7 h-7" />
              Payment Portal
            </h1>
            <p className="text-gray-500">Confirm payments for all orders (except POS in-store)</p>
          </div>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-700">
                {orders.filter(o => o.payment_status === 'pending').length}
              </p>
              <p className="text-sm text-yellow-600">Pending Confirmation</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {orders.filter(o => o.payment_status === 'confirmed').length}
              </p>
              <p className="text-sm text-green-600">Confirmed Today</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
              <XCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">
                {orders.filter(o => o.payment_status === 'rejected').length}
              </p>
              <p className="text-sm text-red-600">Rejected</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by order number or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: 'pending', label: 'Pending', color: 'yellow' },
            { id: 'confirmed', label: 'Confirmed', color: 'green' },
            { id: 'rejected', label: 'Rejected', color: 'red' },
          ].map(f => (
            <Button
              key={f.id}
              variant={filter === f.id ? 'default' : 'outline'}
              onClick={() => setFilter(f.id)}
              className={filter === f.id ? 'bg-[#0a1628]' : ''}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Selected Order - Payment Review */}
      {selectedOrder && (
        <Card className="mb-6 border-2 border-[#0a1628]">
          <div className="p-4 bg-[#0a1628] text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Review Payment - #{selectedOrder.order_number}</h2>
                <p className="text-white/80">{selectedOrder.delivery_info?.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowQR(selectedOrder)}
                >
                  <QrCode className="w-5 h-5 mr-2" />
                  View QR
                </Button>
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={() => {
                    setSelectedOrder(null);
                    setConfirmationNotes('');
                  }}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Order Details */}
              <div>
                <h3 className="font-semibold mb-3">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Total:</strong> ${selectedOrder.total?.toFixed(2)}</p>
                  <p><strong>Subtotal:</strong> ${selectedOrder.subtotal?.toFixed(2)}</p>
                  <p><strong>Tax:</strong> ${selectedOrder.tax?.toFixed(2)}</p>
                  <p><strong>Delivery Fee:</strong> ${selectedOrder.delivery_fee?.toFixed(2)}</p>
                  {selectedOrder.gift_card_discount > 0 && (
                    <p><strong>Gift Card:</strong> -${selectedOrder.gift_card_discount?.toFixed(2)}</p>
                  )}
                </div>
                
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Items ({selectedOrder.items?.length})</h4>
                  <div className="space-y-1 text-sm">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{item.quantity}x {item.name}</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Payment Method Details */}
              <div>
                <h3 className="font-semibold mb-3">Payment Method</h3>
                
                {(() => {
                  const method = getPaymentMethodDisplay(selectedOrder.payment_method);
                  const Icon = method.icon;
                  return (
                    <div className={`p-3 rounded-lg ${method.color} flex items-center gap-3`}>
                      <Icon className="w-6 h-6" />
                      <span className="font-semibold">{method.label}</span>
                    </div>
                  );
                })()}
                
                {/* E-Transfer Details */}
                {selectedOrder.payment_method === 'etransfer' && (
                  <div className="mt-4 space-y-3">
                    {selectedOrder.etransfer_confirmation && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium">E-Transfer Confirmation #:</p>
                        <p className="text-lg font-mono">{selectedOrder.etransfer_confirmation}</p>
                      </div>
                    )}
                    {selectedOrder.etransfer_screenshot && (
                      <div>
                        <p className="text-sm font-medium mb-2">Screenshot:</p>
                        <img 
                          src={selectedOrder.etransfer_screenshot} 
                          alt="E-Transfer Screenshot"
                          className="max-w-full rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Confirmation Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Confirmation Notes {selectedOrder.payment_status === 'pending' && '(Required for rejection)'}
                  </label>
                  <Textarea
                    value={confirmationNotes}
                    onChange={(e) => setConfirmationNotes(e.target.value)}
                    placeholder="Add notes about this payment..."
                    rows={3}
                  />
                </div>
                
                {/* Action Buttons */}
                {selectedOrder.payment_status === 'pending' && (
                  <div className="mt-4 flex gap-3">
                    <Button
                      onClick={() => confirmPayment(selectedOrder.id)}
                      disabled={processing}
                      className="flex-1 bg-green-500 hover:bg-green-600 gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Confirm Payment
                    </Button>
                    <Button
                      onClick={() => rejectPayment(selectedOrder.id)}
                      disabled={processing}
                      variant="destructive"
                      className="flex-1 gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card className="p-8 text-center">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No orders to review</p>
          </Card>
        ) : (
          filteredOrders.map(order => {
            const method = getPaymentMethodDisplay(order.payment_method);
            const Icon = method.icon;
            
            return (
              <Card key={order.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    {/* QR Code Preview */}
                    <div 
                      className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200"
                      onClick={() => setShowQR(order)}
                    >
                      {order.qr_code ? (
                        <img src={order.qr_code} alt="QR" className="w-full h-full object-contain" />
                      ) : (
                        <QrCode className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg">#{order.order_number}</h3>
                        <Badge className={
                          order.payment_status === 'pending' ? 'bg-yellow-500' :
                          order.payment_status === 'confirmed' ? 'bg-green-500' :
                          'bg-red-500'
                        }>
                          {order.payment_status}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${method.color}`}>
                          <Icon className="w-3 h-3" />
                          {method.label}
                        </div>
                        <span className="font-bold text-lg text-[#0a1628]">${order.total?.toFixed(2)}</span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <p className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {order.delivery_info?.name}
                        </p>
                        <p className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {order.payment_status === 'pending' && (
                      <Button
                        onClick={() => setSelectedOrder(order)}
                        className="bg-[#0a1628] gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Review
                      </Button>
                    )}
                    {order.payment_status === 'confirmed' && (
                      <Badge className="bg-green-100 text-green-700 px-3 py-2">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Confirmed
                      </Badge>
                    )}
                    {order.payment_status === 'rejected' && (
                      <Badge className="bg-red-100 text-red-700 px-3 py-2">
                        <XCircle className="w-4 h-4 mr-1" />
                        Rejected
                      </Badge>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Order QR Code</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowQR(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-lg font-semibold mb-2">#{showQR.order_number}</p>
              
              {showQR.qr_code ? (
                <img 
                  src={showQR.qr_code} 
                  alt="Order QR Code" 
                  className="w-64 h-64 mx-auto border-4 border-[#0a1628] rounded-lg"
                />
              ) : (
                <div className="w-64 h-64 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-16 h-16 text-gray-400" />
                </div>
              )}
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>Total:</strong> ${showQR.total?.toFixed(2)}</p>
                <p><strong>Payment:</strong> {getPaymentMethodDisplay(showQR.payment_method).label}</p>
                <p><strong>Status:</strong> {showQR.payment_status}</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PaymentPortal;
